// client/src/components/event/CollabTakeoverConfig.jsx
/**
 * =================================================================
 * Collaboration / Takeover Configuration Component
 * =================================================================
 * Follows ChallengeConfig pattern for consistency.
 * FEATURES:
 * 1. Set type (Collaboration/Takeover), partner info
 * 2. Schedule date and duration
 * 3. Display shareable hyperlink
 * 4. Admin-only type (no public registration)
 * =================================================================
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import DateTimeSelect from "../DateTimeSelect";
import "./AuctionConfig.css";

const CollabTakeoverConfig = ({ tenantId, eventId, itemId, itemName, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const [config, setConfig] = useState({
    name: itemName || "Collaboration",
    type: "COLLABORATION",
    description: "",
    partner_name: "",
    partner_contact: "",
    scheduled_at: "",
    duration_hours: 24,
    status: "NEW",
  });

  const [configExists, setConfigExists] = useState(false);

  const baseUrl = process.env.REACT_APP_BASE_URL || "";
  const shareLink = configExists
    ? `${window.location.origin}/${tenantId}/${eventId}/${itemId}/action?type=collab_takeover`
    : "";

  useEffect(() => {
    loadConfig();
  }, [tenantId, eventId, itemId]);

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return "";
    }
  };

  const formatDateTimeForBackend = (val) => {
    if (!val) return null;
    try {
      const d = new Date(val);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    } catch {
      return null;
    }
  };

  const loadConfig = async () => {
    setLoading(true);
    setMessage({ text: "", type: "" });

    try {
      const response = await axios.get(
        `${baseUrl}/api/collab-takeover/${tenantId}/event/${eventId}/item/${itemId}/config`
      );

      if (response.data.success && response.data.collab_takeover) {
        const c = response.data.collab_takeover;
        setConfigExists(true);
        setConfig({
          name: c.name || itemName || "Collaboration",
          type: c.type || "COLLABORATION",
          description: c.description || "",
          partner_name: c.partner_name || "",
          partner_contact: c.partner_contact || "",
          scheduled_at: formatDateForInput(c.scheduled_at),
          duration_hours: c.duration_hours || 24,
          status: c.status || "NEW",
        });
      } else {
        setConfigExists(false);
        setMessage({
          text: "New Collaboration: fill in the form below, then save.",
          type: "info",
        });
      }
    } catch (err) {
      console.error("Error loading Collab/Takeover config:", err);
      if (err.response?.status === 404) {
        setMessage({
          text: "New Collaboration: fill in the form below, then save.",
          type: "info",
        });
      } else {
        setMessage({ text: "Failed to load configuration", type: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const validateConfig = () => {
    const errors = [];
    if (!config.name || config.name.trim().length === 0) {
      errors.push("Name is required");
    }
    if (config.duration_hours < 1) {
      errors.push("Duration must be at least 1 hour");
    }
    return errors;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const errors = validateConfig();
    if (errors.length > 0) {
      setMessage({ text: errors.join(". "), type: "error" });
      return;
    }

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const payload = {
        name: config.name,
        type: config.type,
        description: config.description || null,
        partner_name: config.partner_name || null,
        partner_contact: config.partner_contact || null,
        scheduled_at: formatDateTimeForBackend(config.scheduled_at),
        duration_hours: Number(config.duration_hours),
        status: config.status,
      };

      const response = await axios.post(
        `${baseUrl}/api/collab-takeover/${tenantId}/event/${eventId}/item/${itemId}/config`,
        payload
      );

      if (response.data.success) {
        setConfigExists(true);
        setMessage({ text: "Configuration saved successfully!", type: "success" });
        setTimeout(() => {
          setMessage({ text: "", type: "" });
          loadConfig();
        }, 2000);
      }
    } catch (err) {
      console.error("Error saving Collab/Takeover config:", err);
      setMessage({
        text: err.response?.data?.message || "Failed to save configuration",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const copyShareLink = () => {
    if (!shareLink) return;
    navigator.clipboard.writeText(shareLink).then(
      () => {
        setMessage({ text: "Link copied to clipboard!", type: "success" });
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      },
      () => setMessage({ text: "Failed to copy link", type: "error" })
    );
  };

  const handleDelete = async () => {
    if (!window.confirm("Cancel this collaboration? This cannot be undone.")) return;

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const response = await axios.post(
        `${baseUrl}/api/collab-takeover/${tenantId}/event/${eventId}/item/${itemId}/config`,
        { ...config, status: "CANCELLED" }
      );
      if (response.data.success) {
        setMessage({ text: "Collaboration cancelled successfully", type: "success" });
        setTimeout(() => {
          if (onClose) onClose();
        }, 1500);
      }
    } catch (err) {
      console.error("Error cancelling Collab/Takeover:", err);
      setMessage({
        text: err.response?.data?.message || "Failed to cancel",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="auction-config-container">
        <div className="loading">Loading configuration...</div>
      </div>
    );
  }

  const isComplete = config.status === "COMPLETE" || config.status === "CANCELLED";
  const typeLabel = config.type === "TAKEOVER" ? "Takeover" : "Collaboration";

  return (
    <div className="auction-config-container">
      <div className="auction-config-header">
        <h2>{typeLabel} Configuration</h2>
        <button className="close-button" onClick={onClose} aria-label="Close">
          &times;
        </button>
      </div>

      {message.text && (
        <div className={`message message-${message.type}`}>{message.text}</div>
      )}

      {shareLink && (
        <div className="share-link-section">
          <h3>Shareable Link</h3>
          <p className="share-link-description">
            Share this link to announce the {typeLabel.toLowerCase()}:
          </p>
          <div className="share-link-container">
            <div className="share-link-display">
              <a href={shareLink} target="_blank" rel="noopener noreferrer">
                {(config.name || itemName || "").replace(/\s+/g, "_")}
              </a>
            </div>
            <button type="button" className="copy-link-button" onClick={copyShareLink}>
              Copy Link
            </button>
          </div>
        </div>
      )}

      <form className="auction-config-form" onSubmit={handleSave}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">
              Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              value={config.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder="Enter name"
              disabled={isComplete}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="type">Type</label>
            <select
              id="type"
              value={config.type}
              onChange={(e) => handleInputChange("type", e.target.value)}
              disabled={isComplete}
            >
              <option value="COLLABORATION">Collaboration</option>
              <option value="TAKEOVER">Takeover</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={config.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            placeholder="Describe the collaboration or takeover"
            disabled={isComplete}
            rows={3}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="partner_name">Partner Name</label>
            <input
              type="text"
              id="partner_name"
              value={config.partner_name}
              onChange={(e) => handleInputChange("partner_name", e.target.value)}
              placeholder="Name of the collaborating partner"
              disabled={isComplete}
            />
          </div>
          <div className="form-group">
            <label htmlFor="partner_contact">Partner Contact</label>
            <input
              type="text"
              id="partner_contact"
              value={config.partner_contact}
              onChange={(e) => handleInputChange("partner_contact", e.target.value)}
              placeholder="Email or social handle"
              disabled={isComplete}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="scheduled_at">Scheduled Date & Time</label>
            <DateTimeSelect
              id="scheduled_at"
              value={config.scheduled_at}
              onChange={(v) => handleInputChange("scheduled_at", v)}
              tenantId={tenantId}
              disabled={isComplete}
            />
          </div>
          <div className="form-group">
            <label htmlFor="duration_hours">
              Duration (hours) <span className="required">*</span>
            </label>
            <input
              type="number"
              id="duration_hours"
              min="1"
              max="720"
              value={config.duration_hours}
              onChange={(e) => handleInputChange("duration_hours", parseInt(e.target.value) || 24)}
              disabled={isComplete}
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="status">Status</label>
          <select
            id="status"
            value={config.status}
            onChange={(e) => handleInputChange("status", e.target.value)}
            disabled={isComplete}
          >
            <option value="NEW">New</option>
            <option value="ACTIVE">Active</option>
            <option value="LIVE">Live</option>
            <option value="COMPLETE">Complete</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>

        <div className="form-actions">
          {isComplete ? (
            <div className="completion-notice">
              This {typeLabel.toLowerCase()} is {config.status === "CANCELLED" ? "cancelled" : "complete"} - no further changes allowed
            </div>
          ) : (
            <>
              <button type="submit" className="save-button" disabled={saving}>
                {saving ? "Saving..." : "Save Configuration"}
              </button>
              {configExists && (
                <button
                  type="button"
                  className="delete-button"
                  onClick={handleDelete}
                  disabled={saving}
                >
                  Cancel {typeLabel}
                </button>
              )}
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default CollabTakeoverConfig;
